import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	cancelFrameChatAction,
	enqueueFrameChatAction,
	FrameChatActionEntry,
	FrameChatActionPayload,
} from 'redux/mode/chatActionQueue';
import { RootReducer } from 'redux/reducers';
import { v4 as uuid } from 'uuid';

// See useDsChatQueue for the conceptual explanation. Mirrors it for frame chats.

type EnqueueArgs = {
	label: string;
	clientKey?: string;
	payload: FrameChatActionPayload;
};

type Result = {
	items: FrameChatActionEntry[];
	enqueue: (args: EnqueueArgs) => void;
	cancel: (id: string) => void;
};

export const useFrameChatQueue = (chatId: string | null): Result => {
	const dispatch = useDispatch();

	const items = useSelector(
		(r: RootReducer) =>
			chatId ? r.mode.frameChatActionQueue.filter((e) => e.chatId === chatId) : [],
		shallowEqual
	);

	const enqueue = useCallback(
		(args: EnqueueArgs) => {
			if (!chatId) return;
			dispatch(
				enqueueFrameChatAction({
					id: uuid(),
					chatId,
					createdAt: Date.now(),
					label: args.label,
					...(args.clientKey != null && { clientKey: args.clientKey }),
					payload: args.payload,
				})
			);
		},
		[chatId, dispatch]
	);

	const cancel = useCallback(
		(id: string) => {
			dispatch(cancelFrameChatAction({ id }));
		},
		[dispatch]
	);

	return useMemo(() => ({ items, enqueue, cancel }), [items, enqueue, cancel]);
};
